import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Send, Loader2, Sparkles, ListChecks, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { authedApi } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const CATEGORIES = ["News", "Glitch", "Retro", "Community", "Rare"];
const STATUS_LABEL = { open: "Offen", in_progress: "In Bearbeitung", released: "Veroeffentlicht", rejected: "Abgelehnt" };
const STATUS_COLOR = {
  open: "bg-[#7c4dff]/15 text-[#b39bff]",
  in_progress: "bg-[#ffb84d]/15 text-[#ffb84d]",
  released: "bg-emerald-500/15 text-emerald-300",
  rejected: "bg-rose-500/15 text-rose-300",
};

const MyStories = () => {
  const { user, userToken, adminToken, adminName, ready } = useAuth();
  const token = userToken || adminToken;
  const isAdmin = !!adminToken;
  const { toast } = useToast();
  const a = useMemo(() => (token ? authedApi(token) : null), [token]);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("News");
  const [submitting, setSubmitting] = useState(false);
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");

  const load = async () => {
    if (!a) return;
    try { setStories(await a.listMyStories()); } catch { /* ignore */ }
  };

  useEffect(() => { load();  }, [a]);

  const loadMessages = async (s) => {
    setSelected(s);
    setMessages([]);
    try { setMessages(await a.listStoryMessages(s.id)); } catch { /* ignore */ }
  };

  const onSubmit = async () => {
    if (!a) return;
    if (title.trim().length < 3) return toast({ title: "Titel zu kurz", variant: "destructive" });
    if (body.trim().length < 10) return toast({ title: "Story zu kurz", variant: "destructive" });
    setSubmitting(true);
    try {
      await a.submitStory({ title: title.trim(), body: body.trim(), category });
      toast({ title: "Story eingereicht", description: "Das Team wird sich melden!" });
      setTitle(""); setBody(""); setCategory("News");
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Konnte nicht senden", variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const onSend = async () => {
    if (!msgText.trim() || !selected) return;
    try {
      await a.postStoryMessage(selected.id, msgText.trim());
      setMsgText("");
      setMessages(await a.listStoryMessages(selected.id));
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  if (!ready) return <div className="mx-auto max-w-7xl px-4 py-20 text-center text-zinc-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  if (!token) return <Navigate to="/login?next=/meine-stories" replace />;
  const isVerified = isAdmin || !!user?.verified;
  if (!isVerified) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-12 text-center" data-testid="stories-not-verified">
        <Sparkles className="mx-auto h-10 w-10 text-[#ffb84d]" />
        <h1 className="mt-4 text-2xl font-bold text-white">Bitte erst Habbo verifizieren</h1>
        <p className="mt-2 text-sm text-zinc-400">Stories koennen nur verifizierte Habbos einreichen. Verifizier dich jetzt ueber dein Habbo-Motto.</p>
        <Link to="/profil" className="mt-6 inline-block btn-primary rounded-lg px-4 py-2 text-sm font-semibold">Zur Verifizierung →</Link>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-4 py-8" data-testid="my-stories-page">
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">Story-Einreichung</div>
      <h1 className="mt-1 text-3xl font-bold text-white">Meine Stories</h1>
      <p className="mt-1 text-sm text-zinc-400">Reich deine Habbo-Geschichten ein und chatte mit dem Team bis zur Veroeffentlichung.</p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[2fr_3fr]">
        {/* Submit Form */}
        <div className="rounded-2xl border border-[#7c4dff]/30 bg-gradient-to-br from-[#1a142e] via-[#0f141b] to-[#0f141b] p-5">
          <h2 className="text-lg font-bold text-white">Neue Story einreichen</h2>
          <div className="mt-3 space-y-3">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel..." maxLength={200} data-testid="story-title" className="bg-[#10141b] border-white/10 text-zinc-100" />
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="story-category" className="w-full rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-100">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Deine Story – was ist passiert, wann, wer war dabei? Je mehr Details, desto besser." rows={8} maxLength={4000} data-testid="story-body" className="bg-[#10141b] border-white/10 text-zinc-200" />
            <Button onClick={onSubmit} disabled={submitting || !title.trim() || body.trim().length < 10} data-testid="story-submit" className="btn-primary inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Story einreichen
            </Button>
          </div>
        </div>

        {/* Liste + Chat */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#0f141b] p-4">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-300"><ListChecks className="h-4 w-4" /> Deine Einreichungen ({stories.length})</h2>
            </div>
            {stories.length === 0 ? (
              <div className="py-8 text-center text-xs text-zinc-500">Noch keine Stories eingereicht.</div>
            ) : (
              <div className="mt-3 space-y-2">
                {stories.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => loadMessages(s)}
                    data-testid={`my-story-${s.id}`}
                    className={`w-full text-left rounded-lg border p-3 transition-colors ${selected?.id === s.id ? "border-[#7c4dff]/40 bg-[#7c4dff]/10" : "border-white/5 bg-[#10141b] hover:border-white/15"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="truncate text-sm font-semibold text-white">{s.title}</div>
                      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_COLOR[s.status]}`}>{STATUS_LABEL[s.status]}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">{s.category} · {new Date(s.created_at).toLocaleDateString("de-DE")}</div>
                    {s.status === "released" && s.released_article_id && (
                      <Link to={`/news/${s.released_article_id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> Zur veroeffentlichten News <ArrowRight className="h-3 w-3" />
                      </Link>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chat */}
          {selected && (
            <div className="rounded-2xl border border-white/10 bg-[#0f141b]" data-testid="story-chat">
              <div className="border-b border-white/10 px-4 py-3">
                <div className="text-xs font-bold uppercase tracking-wider text-[#b39bff]">Chat zur Story</div>
                <div className="text-sm font-semibold text-white">{selected.title}</div>
              </div>
              <div className="max-h-[300px] space-y-2 overflow-y-auto p-3">
                {messages.length === 0 ? (
                  <div className="py-6 text-center text-xs text-zinc-500">Noch keine Nachrichten. Schreib dem Team!</div>
                ) : messages.map((m) => (
                  <div key={m.id} className={`flex flex-col gap-0.5 rounded-lg p-2 text-xs ${m.role === "submitter" ? "bg-[#7c4dff]/10" : "bg-[#10141b] border border-white/5"}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`font-bold ${m.role === "submitter" ? "text-[#b39bff]" : "text-emerald-300"}`}>{m.author}{m.role === "admin" ? " (Admin)" : m.role === "team" ? " (Team)" : ""}</span>
                      <span className="text-[10px] text-zinc-500">{new Date(m.created_at).toLocaleString("de-DE")}</span>
                    </div>
                    <p className="whitespace-pre-line text-zinc-200">{m.text}</p>
                  </div>
                ))}
              </div>
              {selected.status !== "released" && selected.status !== "rejected" ? (
                <div className="flex gap-2 border-t border-white/10 p-3">
                  <Input value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") onSend(); }} placeholder="Nachricht schreiben..." data-testid="story-chat-input" className="bg-[#10141b] border-white/10 text-zinc-200" />
                  <Button onClick={onSend} disabled={!msgText.trim()} data-testid="story-chat-send" className="btn-primary rounded-lg px-3 py-2 text-sm"><Send className="h-4 w-4" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 border-t border-white/10 px-3 py-2 text-xs text-zinc-500">
                  {selected.status === "released" ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" /> Diese Story ist veroeffentlicht – Chat geschlossen.</> : <><XCircle className="h-3.5 w-3.5 text-rose-300" /> Diese Story wurde nicht uebernommen.</>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MyStories;
